import React, { forwardRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import dayjs from "dayjs";
import * as Yup from "yup";

import { TextField } from "@mui/material";
import { ButtonsWrapper, StyledForm } from "./styled";

import { getRequiredField } from "helpers/formValidations";
import { METERS_CONFIG, METERS_PHRASE } from "./config";

import RegularButton from "components/CustomButtons/Button";
import AutocompleteControlled from "components/AutocompleteControlled/AutocompleteControlled";
import {
  fetchNewMeter,
  fetchEditMeter,
} from "redux/actions/buildings/meters/metersAction";
import { getModelsByBrand } from "redux/actions/buildings/brands/brandsMetersActions";

const MetersSchema = Yup.object({
  [METERS_CONFIG.serialNumber.key]: Yup.string().required(
    getRequiredField(METERS_CONFIG.serialNumber.label)
  ),
  [METERS_CONFIG.brand.key]: Yup.object().required(
    getRequiredField(METERS_CONFIG.brand.label)
  ),
  [METERS_CONFIG.model.key]: Yup.object().required(
    getRequiredField(METERS_CONFIG.model.label)
  ),
  [METERS_CONFIG.capture.key]: Yup.string()
    .required(getRequiredField(METERS_CONFIG.capture.label))
    .matches(
      /^\d*\.?\d{0,3}$/,
      `${METERS_CONFIG.capture.label} debe tener un máximo de 3 decimales.`
    ),
  [METERS_CONFIG.releaseDate.key]: Yup.date("")
    .typeError(getRequiredField(METERS_CONFIG.releaseDate.label))
    .required(getRequiredField(METERS_CONFIG.releaseDate.label)),
  [METERS_CONFIG.status.key]: Yup.object().required(
    getRequiredField(METERS_CONFIG.status.label)
  ),
});

export const MetersForm = forwardRef((props) => {
  const dispatch = useDispatch();
  const { handleClose, stationsSelected } = props;
  const { brandsList, modelsList, modelsByBrand } = useSelector(
    (state) => state.brandsMeters
  );
  const { isAddMode, deleteMode, editMeter, meterStatuses } = useSelector(
    (state) => state.meters
  );

  const handleCloseForm = () => {
    handleClose();
    reset();
  };

  const {
    reset,
    watch,
    register,
    handleSubmit,
    setValue,
    control,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(MetersSchema),
  });
  const watchFields = watch([METERS_CONFIG.brand.key]);

  const onSubmit = React.useCallback(
    (formData) => {
      // You can use the validated data to make your API request here
      const body = {
        serialNumber: formData[METERS_CONFIG.serialNumber.key],
        idModel: formData[METERS_CONFIG.model.key].id,
        installationDate: formData[METERS_CONFIG.installationDate.key]
          ? dayjs(formData[METERS_CONFIG.installationDate.key]).format(
              "YYYY-MM-DD"
            )
          : null,
        registrationDate: dayjs(formData[METERS_CONFIG.releaseDate.key]).format(
          "YYYY-MM-DD"
        ),
        currentCapture: Number(formData[METERS_CONFIG.capture.key]),
        status: formData[METERS_CONFIG.status.key].value,
        idStation: stationsSelected.id,
      };

      if (isAddMode) {
        dispatch(fetchNewMeter(body));
        handleCloseForm();
      } else {
        dispatch(fetchEditMeter(body, editMeter.id));
        handleCloseForm();
      }

      reset();
    },
    [reset, isAddMode, editMeter]
  );

  useEffect(() => {
    if (editMeter) {
      const editModel = modelsList.find(
        (model) => model.id === editMeter.idModel
      );

      const editBrand = brandsList.find(
        (brand) => brand.id === editModel.idBrand
      );

      const editStatus = meterStatuses.find(
        (status) => status.value === editMeter.status
      );

      const installationDate = editMeter[METERS_CONFIG.installationDate.key]
        ? dayjs(editMeter[METERS_CONFIG.installationDate.key]).format(
            "YYYY-MM-DD"
          )
        : null;
      setValue(METERS_CONFIG.installationDate.key, installationDate);

      const editDate = dayjs(editMeter[METERS_CONFIG.releaseDate.key]).format(
        "YYYY-MM-DD"
      );
      setValue(METERS_CONFIG.releaseDate.key, editDate);

      setValue(
        METERS_CONFIG.serialNumber.key,
        editMeter[METERS_CONFIG.serialNumber.key]
      );

      setValue(METERS_CONFIG.capture.key, editMeter[METERS_CONFIG.capture.key]);

      setValue(METERS_CONFIG.model.key, editModel);
      setValue(METERS_CONFIG.brand.key, editBrand);
      setValue(METERS_CONFIG.status.key, editStatus);
    }
  }, [editMeter]);

  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (value && name === METERS_CONFIG.brand.key) {
        dispatch(getModelsByBrand(value.brand.id));
      }
    });
    return () => subscription.unsubscribe();
  }, [watchFields]);

  useEffect(() => {
    if (isAddMode) {
      reset();
    }
  }, [isAddMode]);

  return (
    <>
      <StyledForm component="form" fullWidth onSubmit={handleSubmit(onSubmit)}>
        <TextField
          fullWidth
          type="text"
          label={METERS_CONFIG.serialNumber.label}
          variant="outlined"
          error={!!errors[METERS_CONFIG.serialNumber.key]}
          helperText={errors[METERS_CONFIG.serialNumber.key]?.message}
          {...register(METERS_CONFIG.serialNumber.key)}
          size="small"
        />

        <AutocompleteControlled
          name={METERS_CONFIG.brand.key}
          label={METERS_CONFIG.brand.label}
          options={brandsList}
          control={control}
        />

        <AutocompleteControlled
          name={METERS_CONFIG.model.key}
          label={METERS_CONFIG.model.label}
          options={modelsByBrand}
          control={control}
        />

        <TextField
          fullWidth
          type="date"
          label={METERS_CONFIG.installationDate.label}
          variant="outlined"
          error={!!errors[METERS_CONFIG.installationDate.key]}
          helperText={errors[METERS_CONFIG.installationDate.key]?.message}
          {...register(METERS_CONFIG.installationDate.key)}
          InputLabelProps={{
            shrink: true,
          }}
          size="small"
        />

        <TextField
          fullWidth
          type="date"
          label={METERS_CONFIG.releaseDate.label}
          variant="outlined"
          error={!!errors[METERS_CONFIG.releaseDate.key]}
          helperText={errors[METERS_CONFIG.releaseDate.key]?.message}
          {...register(METERS_CONFIG.releaseDate.key)}
          InputLabelProps={{
            shrink: true,
          }}
          size="small"
        />

        <TextField
          fullWidth
          type="number"
          label={METERS_CONFIG.capture.label}
          variant="outlined"
          error={!!errors[METERS_CONFIG.capture.key]}
          helperText={errors[METERS_CONFIG.capture.key]?.message}
          {...register(METERS_CONFIG.capture.key)}
          InputLabelProps={{
            shrink: true,
          }}
          inputProps={{
            step: "0.001", // Permite hasta 3 decimales
          }}
          size="small"
        />

        <AutocompleteControlled
          name={METERS_CONFIG.status.key}
          label={METERS_CONFIG.status.label}
          options={meterStatuses}
          control={control}
        />

        <ButtonsWrapper>
          <RegularButton
            variant="contained"
            color="danger"
            onClick={handleClose}
          >
            {METERS_PHRASE.modal.actions.cancel}
          </RegularButton>
          <RegularButton
            variant="contained"
            type="submit"
            color="info"
          >
            {isAddMode
              ? METERS_PHRASE.modal.actions.add
              : deleteMode
              ? METERS_PHRASE.modal.actions.delete
              : METERS_PHRASE.modal.actions.save}
          </RegularButton>
        </ButtonsWrapper>
      </StyledForm>
    </>
  );
});
